import Video from 'twilio-video';

/**
 * A blank video track, published on PHONE appointments in place of the GP's camera.
 *
 * Why this exists. The patient apps decide "the GP has arrived" from the GP's VIDEO track, and the
 * live Flutter app additionally requires that track to be ENABLED at the moment it subscribes. The
 * call page used to publish the real camera and then disable it a moment later, so on phone
 * appointments the disable usually won that race: the patient sat in the waiting room while the GP
 * was already in the call, until Flutter's 2-minute refresh happened to reconnect - a median of about
 * a minute, sometimes two and a half, and sometimes only fixed by the patient pressing back and
 * starting again. Measured over Twilio's room logs for 8-15 Sep 2026: 9 of 85 Flutter consultations.
 *
 * A blank track that stays enabled for the whole call is detected immediately, shows the patient
 * nothing, and lets the GP's camera be stopped rather than merely muted.
 *
 * Returns null when the browser cannot capture a canvas (and under jsdom), so the caller can fall
 * back to the old disable-the-camera behaviour instead of publishing a live camera.
 */

export const PLACEHOLDER_TRACK_NAME = 'gp-audio-placeholder';

const WIDTH = 320;
const HEIGHT = 240;
// A canvas only produces a frame when something draws to it. One frame can be treated as a stalled
// track, so redraw the same frame slowly: cheap, and the track stays unambiguously live.
const REDRAW_MS = 2000;

export function createPlaceholderVideoTrack() {
    try {
        if (typeof document === 'undefined' || typeof Video.LocalVideoTrack !== 'function') {
            return null;
        }
        const canvas = document.createElement('canvas');
        canvas.width = WIDTH;
        canvas.height = HEIGHT;
        if (typeof canvas.captureStream !== 'function' || typeof canvas.getContext !== 'function') {
            return null;
        }
        const context = canvas.getContext('2d');
        if (!context) {
            return null;
        }

        const draw = () => {
            context.fillStyle = '#0b1220';
            context.fillRect(0, 0, WIDTH, HEIGHT);
        };
        draw();

        const stream = canvas.captureStream(1);
        const mediaStreamTrack = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
        if (!mediaStreamTrack) {
            return null;
        }

        const timer = setInterval(draw, REDRAW_MS);
        const track = new Video.LocalVideoTrack(mediaStreamTrack, { name: PLACEHOLDER_TRACK_NAME });

        return {
            track,
            stop() {
                clearInterval(timer);
                try {
                    track.stop();
                } catch (e) {
                    /* already stopped */
                }
                try {
                    mediaStreamTrack.stop();
                } catch (e) {
                    /* already stopped */
                }
            },
        };
    } catch (e) {
        // Never let a cosmetic track stop a consultation from starting.
        return null;
    }
}

/** Is this publication the blank tile rather than a real camera? */
export function isPlaceholderPublication(publication) {
    if (!publication) {
        return false;
    }
    const name = publication.trackName || (publication.track && publication.track.name);
    return name === PLACEHOLDER_TRACK_NAME;
}
